import React, { useState } from "react";
import * as Mui from "@mui/material";
import { AccountBalanceOutlined, CurrencyRupeeOutlined, FunctionsOutlined, GroupsOutlined, PersonOutlineOutlined } from "@mui/icons-material";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { detailCardSx } from "components/DesignSystem/RequestDetailField";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { SimpleTable, searchFieldSx, tableCellSx } from "pages/PayRoll/Salary/Shared/salaryUi";
import { StatCardGrid } from "pages/PayRoll/Shared/statutoryUi";
import { ACCOUNTS, CODES, accountsFor, challanTotal, codeLabel, formatDay, money, num, reconStatus } from "./pfReportsData";

// Overview (P7): totals, one row per report (clicking opens its tab) and
// what the EPFO challan will show per account.

const STATUS_COLORS = {
  Available: "#12B76A",
  Blocked: "#DC2626",
  Ready: "#2563EB",
  Generated: "#12B76A",
  "To do": "#F0954A",
  Done: "#12B76A",
  "Action needed": "#DC2626",
  "All clear": "#12B76A",
};

const linkSx = { color: designColors.tealDark, fontWeight: designWeights.semibold, cursor: "pointer", whiteSpace: "nowrap" };

export const PfOverviewTab = ({ month, monthInfo, rows, register, exitRows, issues, payableFor, blockingFor, challans, ecrFiles, codeFilter, goTo }) => {
  const [query, setQuery] = useState("");
  const codes = CODES.filter((code) => codeFilter === "all" || String(code.id) === codeFilter);
  const sum = (key) => rows.reduce((total, row) => total + row[key], 0);
  const employee = sum("ee_pf") + sum("vpf");
  const employer = sum("er_eps") + sum("er_epf");
  const perCode = codes.map((code) => ({ code, accounts: accountsFor(rows.filter((row) => row.code_id === code.id)) }));
  const totalPayable = perCode.reduce((total, item) => total + item.accounts.total, 0);

  const recon = codes.map((code) => {
    const payable = payableFor(code.id, month).total;
    const paid = challans.filter((item) => item.code_id === code.id && item.month === month).reduce((total, item) => total + challanTotal(item), 0);
    return { code, status: reconStatus(payable, paid), paid };
  });
  const paidCount = recon.filter((item) => item.status === "Paid" || item.status === "Overpaid").length;
  const blocking = issues.filter((issue) => issue.severity === "Blocking").length;
  const warnings = issues.filter((issue) => issue.severity === "Warning").length;
  const infos = issues.filter((issue) => issue.severity === "Info").length;
  const toMark = exitRows.filter((row) => row.status !== "Marked on portal").length;

  const reports = [
    {
      report: "Contribution Register",
      code: "All codes",
      details: `${rows.length} members · totals match payroll`,
      status: "Available",
      action: "View · Download",
      tab: "register",
    },
    ...codes.map((code) => {
      const blockers = blockingFor(code.id);
      const file = ecrFiles.find((item) => item.code_id === code.id && item.month === month && item.status === "Current");
      const uan = blockers.filter((issue) => issue.rule === "UAN_MISSING").length;
      const dob = blockers.filter((issue) => issue.rule === "DOB_MISSING").length;
      return {
        report: "ECR File",
        code: codeLabel(code),
        details: blockers.length
          ? `Blocked: ${[uan ? `${uan} UAN missing` : "", dob ? `${dob} date of birth missing` : ""].filter(Boolean).join(", ")}`
          : file
            ? `Version ${file.version} · ${file.lines} members · generated ${formatDay(file.generated_at)}`
            : `${register.filter((row) => row.code_id === code.id).length} members · ready to generate`,
        status: blockers.length ? "Blocked" : file ? "Generated" : "Ready",
        action: blockers.length ? "Preview · See validation" : "View · Download",
        tab: blockers.length ? "validation" : "ecr",
      };
    }),
    {
      report: "Exits for EPFO portal",
      code: "All codes",
      details: `${exitRows.length} employees left in ${monthInfo.label.split(" ")[0]} — ${toMark ? `${toMark} to mark exit on the portal` : "all marked"}`,
      status: toMark ? "To do" : "Done",
      action: "View · Download",
      tab: "exits",
    },
    {
      report: "Reconciliation",
      code: "All codes",
      details: recon.map((item) => `${item.code.type === "MAIN" ? "Main" : "Extension"} ${item.status === "Not recorded" ? "not yet recorded" : `${item.status.toLowerCase()} ${money(item.paid)}`}`).join(" · "),
      status: `${paidCount} of ${recon.length} paid`,
      action: "View",
      tab: "reconciliation",
    },
    {
      report: "Validation",
      code: "All codes",
      details: `${blocking} blocking · ${warnings} warning · ${infos} info`,
      status: blocking ? "Action needed" : "All clear",
      action: "View",
      tab: "validation",
    },
  ];
  const needle = query.trim().toLowerCase();
  const shownReports = needle ? reports.filter((item) => Object.values(item).some((value) => String(value).toLowerCase().includes(needle))) : reports;
  const statusColor = (status) => STATUS_COLORS[status] || (status.startsWith(`${recon.length} of`) ? "#12B76A" : "#F0954A");

  return (
    <Mui.Stack spacing={2}>
      <StatCardGrid
        cards={[
          { key: "members", title: "PF members", value: num(rows.length), icon: GroupsOutlined, tone: "blue" },
          { key: "wages", title: "PF wages", value: money(sum("epf_wage")), icon: CurrencyRupeeOutlined, tone: "green" },
          { key: "employee", title: "Employee share", value: money(employee), icon: PersonOutlineOutlined, tone: "purple" },
          { key: "employer", title: "Employer share", value: money(employer), icon: AccountBalanceOutlined, tone: "amber" },
          { key: "total", title: "Total payable", value: money(totalPayable), icon: FunctionsOutlined, tone: "pink" },
        ]}
      />

      <Mui.Box sx={detailCardSx}>
        <Mui.Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1} sx={{ mb: 1.5 }}>
          <Mui.Typography sx={designType.sectionHeading}>Reports — {monthInfo.label}</Mui.Typography>
          <Mui.TextField size="small" placeholder="Search" value={query} onChange={(event) => setQuery(event.target.value)} sx={searchFieldSx} />
        </Mui.Stack>
        <SimpleTable
          fit
          heads={[
            { label: "Report", width: 180 },
            { label: "PF Code", width: 220 },
            { label: "Details", width: 300 },
            { label: "Status", width: 120 },
            { label: "Action", width: 160 },
          ]}
        >
          {shownReports.map((item) => (
            <Mui.TableRow key={`${item.report}-${item.code}`} hover onClick={() => goTo(item.tab)} sx={{ cursor: "pointer" }}>
              <Mui.TableCell sx={{ ...tableCellSx, fontWeight: designWeights.medium }}>{item.report}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{item.code}</Mui.TableCell>
              <Mui.TableCell sx={{ ...tableCellSx, color: designColors.slate600 }}>{item.details}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <RequestStatusChip status={item.status} colorCode={statusColor(item.status)} />
              </Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>
                <Mui.Box component="span" sx={linkSx}>{item.action}</Mui.Box>
              </Mui.TableCell>
            </Mui.TableRow>
          ))}
        </SimpleTable>
        <Mui.Typography sx={{ ...designType.captionText, mt: 1.5 }}>
          Reports are live views of the finalised payroll. View any report or file on screen before downloading. Only generated ECR files are stored, with version and time.
        </Mui.Typography>
      </Mui.Box>

      <Mui.Box sx={detailCardSx}>
        <Mui.Typography sx={designType.sectionHeading}>Payable by challan account — {monthInfo.label}</Mui.Typography>
        <Mui.Typography sx={{ ...designType.helperText, mt: 0.25, mb: 1.5 }}>What the EPFO challan will show</Mui.Typography>
        <SimpleTable
          fit
          heads={[
            { label: "Account", width: 90 },
            { label: "What it is", width: 220 },
            ...perCode.map((item) => ({ label: `${item.code.type === "MAIN" ? "Main" : "Extension"} (₹)`, align: "right", width: 120 })),
            { label: "Total (₹)", align: "right", width: 120 },
          ]}
        >
          {ACCOUNTS.map((account) => (
            <Mui.TableRow key={account.key}>
              <Mui.TableCell sx={tableCellSx}>{account.label}</Mui.TableCell>
              <Mui.TableCell sx={tableCellSx}>{account.what}</Mui.TableCell>
              {perCode.map((item) => (
                <Mui.TableCell key={item.code.id} align="right" sx={tableCellSx}>{num(item.accounts[account.key])}</Mui.TableCell>
              ))}
              <Mui.TableCell align="right" sx={tableCellSx}>{num(perCode.reduce((total, item) => total + item.accounts[account.key], 0))}</Mui.TableCell>
            </Mui.TableRow>
          ))}
          <Mui.TableRow>
            <Mui.TableCell sx={{ ...tableCellSx, fontWeight: designWeights.heading }}>Total</Mui.TableCell>
            <Mui.TableCell sx={tableCellSx} />
            {perCode.map((item) => (
              <Mui.TableCell key={item.code.id} align="right" sx={{ ...tableCellSx, fontWeight: designWeights.heading }}>{num(item.accounts.total)}</Mui.TableCell>
            ))}
            <Mui.TableCell align="right" sx={{ ...tableCellSx, fontWeight: designWeights.heading }}>{num(totalPayable)}</Mui.TableCell>
          </Mui.TableRow>
        </SimpleTable>
      </Mui.Box>
    </Mui.Stack>
  );
};
